import React, { useState, useEffect } from 'react';
import { Drawer, Form, Button, SelectPicker, CheckPicker, Message, useToaster } from 'rsuite';
import { useTranslation } from 'react-i18next';

const FilterDrawer = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  withPriority,
  users,
  zones
}) => {
  const { t } = useTranslation();
  const [formValue, setFormValue] = useState(currentFilters);
  const toaster = useToaster();

  const getInitialFilters = () => {
    return withPriority
      ? { user: '', zone: '', priority: null, dayOfWeek: null }
      : { user: '', zones: [] };
  };

  const userOptions = React.useMemo(() => {
    return (
      users?.map(user => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user._id
      })) || []
    );
  }, [users]);

  const zoneOptions = React.useMemo(() => {
    return (
      zones?.map(zone => ({
        label: zone.name,
        value: zone._id
      })) || []
    );
  }, [zones]);

  const priorityOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
  ];

  const dayOfWeekOptions = [
    { label: t('Monday'), value: 1 },
    { label: t('Tuesday'), value: 2 },
    { label: t('Wednesday'), value: 3 },
    { label: t('Thursday'), value: 4 },
    { label: t('Friday'), value: 5 },
    { label: t('Saturday'), value: 6 },
    { label: t('Sunday'), value: 0 }
  ];

  const handleSubmit = () => {
    // Check if any filters are set
    const hasFilters = Object.entries(formValue).some(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (key === 'priority' || key === 'dayOfWeek') {
        return value !== null && value !== undefined && value !== '';
      }
      return value !== '' && value !== null && value !== undefined;
    });

    if (hasFilters) {
      // Convert priority and dayOfWeek to numbers if they exist
      const processedFormValue = { ...formValue };
      if (processedFormValue.priority) {
        processedFormValue.priority = Number(processedFormValue.priority);
      }
      if (processedFormValue.dayOfWeek) {
        processedFormValue.dayOfWeek = Number(processedFormValue.dayOfWeek);
      }

      onFiltersChange(processedFormValue);
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    const initialFilters = getInitialFilters();
    setFormValue(initialFilters);
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>
    );
    onClose();
  };

  const handleZoneChange = value => {
    setFormValue(prev => ({
      ...prev,
      zone: value,
      priority: value ? prev.priority : null,
      dayOfWeek: value ? prev.dayOfWeek : null
    }));
  };

  useEffect(() => {
    if (open) {
      setFormValue(currentFilters || getInitialFilters());
    }
  }, [open, currentFilters, withPriority]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User')}</Form.ControlLabel>
            <Form.Control
              name="user"
              accepter={SelectPicker}
              data={userOptions}
              searchable
              placeholder={t("Select a user")}
              block
              cleanable
            />
          </Form.Group>

          {withPriority ? (
            // Priority-based filters
            <>
              <Form.Group>
                <Form.ControlLabel>Zone</Form.ControlLabel>
                <Form.Control
                  name="zone"
                  accepter={SelectPicker}
                  data={zoneOptions}
                  searchable
                  placeholder={t("Select a zone")}
                  block
                  cleanable
                  onChange={handleZoneChange}
                />
              </Form.Group>

              {formValue.zone && (
                <>
                  <Form.Group>
                    <Form.ControlLabel>{t('Day of Week')}</Form.ControlLabel>
                    <Form.Control
                      name="dayOfWeek"
                      accepter={SelectPicker}
                      data={dayOfWeekOptions}
                      placeholder={t("Select day of week")}
                      block
                      cleanable
                      placement="auto"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Priority')}</Form.ControlLabel>
                    <Form.Control
                      name="priority"
                      accepter={SelectPicker}
                      data={priorityOptions}
                      placeholder={t("Select priority")}
                      block
                      cleanable
                    />
                  </Form.Group>
                </>
              )}
            </>
          ) : (
            <Form.Group>
              <Form.ControlLabel>Zones</Form.ControlLabel>
              <Form.Control
                name="zones"
                accepter={CheckPicker}
                data={zoneOptions}
                searchable
                placeholder={t("Select zones")}
                block
                sticky
                placement="auto"
                menuMaxHeight={200}
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default FilterDrawer;
